<template>
  <section
    class="ds-program-help-card"
    :class="`ds-program-help-card--${variant}`"
    :aria-labelledby="titleId"
  >
    <div class="ds-program-help-card__header">
      <img
        v-if="variant === 'media' && avatarSrc"
        class="ds-program-help-card__avatar"
        :src="avatarSrc"
        :alt="avatarAlt"
        :aria-hidden="avatarAlt ? undefined : 'true'"
      >

      <div class="ds-program-help-card__heading">
        <img
          v-if="variant === 'media' && logoSrc"
          class="ds-program-help-card__logo"
          :src="logoSrc"
          :alt="logoAlt || ''"
          :style="logoStyle"
        >

        <h2
          :id="titleId"
          class="ds-program-help-card__title"
        >
          <slot name="title">
            {{ title }}
          </slot>
        </h2>
      </div>
    </div>

    <div class="ds-program-help-card__description">
      <slot />
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'

/*
  `logoWidth`/`logoHeight` permiten ajustar la caja del logo al aspecto de
  cada programa: los assets no comparten proporción. Sin ellas se mantiene
  el tamaño original (135x40).
*/
const props = withDefaults(defineProps<{
  avatarSrc?: string
  avatarAlt?: string
  logoSrc?: string
  logoAlt?: string
  logoWidth?: number
  logoHeight?: number
  title: string
  titleId?: string
  variant?: 'media' | 'info'
}>(), {
  avatarSrc: undefined,
  avatarAlt: '',
  logoSrc: undefined,
  logoAlt: '',
  logoWidth: undefined,
  logoHeight: undefined,
  titleId: 'program-help-card-title',
  variant: 'media'
})

const logoStyle = computed(() => ({
  ...(props.logoWidth ? { width: `${props.logoWidth}px` } : {}),
  ...(props.logoHeight ? { height: `${props.logoHeight}px` } : {})
}))
</script>

<style scoped>
.ds-program-help-card {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 16px;
  box-sizing: border-box;
  background: var(--ds-grey-200);
  border-radius: 24px;
}

.ds-program-help-card__header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.ds-program-help-card__avatar {
  display: block;
  flex: 0 0 88px;
  width: 88px;
  height: 88px;
  object-fit: cover;
  border: 1px solid var(--ds-grey-50);
  border-radius: 50%;
}

.ds-program-help-card__heading,
.ds-program-help-card__description {
  display: flex;
  flex-direction: column;
}

.ds-program-help-card--info {
  background: var(--ds-surface-info);
  border: 1px solid var(--ds-info-100);
}

.ds-program-help-card--info .ds-program-help-card__heading {
  gap: 0;
}

.ds-program-help-card--info .ds-program-help-card__title {
  font-size: var(--ds-h3-size);
  font-weight: var(--ds-h3-weight);
  line-height: 44px;
}

.ds-program-help-card__heading {
  flex: 1 1 auto;
  gap: 8px;
  min-width: 0;
}

.ds-program-help-card__logo {
  display: block;
  width: 135px;
  height: 40px;
  object-fit: contain;
}

.ds-program-help-card__title {
  margin: 0;
  color: var(--ds-purple-900);
  font-family: var(--ds-font-family-bebas);
  font-size: var(--ds-bebas4-size);
  font-weight: var(--ds-bebas4-weight);
  line-height: 40px;
}

.ds-program-help-card__description {
  gap: 24px;
  color: var(--ds-text-primary);
  font-size: var(--ds-body3-size);
  font-weight: var(--ds-body3-weight);
  line-height: 24px;
}

@media (min-width: 768px) {
  .ds-program-help-card--info {
    padding: 24px;
  }
}

@media (min-width: 1208px) {
  .ds-program-help-card {
    padding: 24px;
  }
}
</style>
